/**
 * Confirmed corridor fares.
 * Missing vehicle keys are unpublished. Do not fill them with estimates.
 * oneWayPackage is used only while One Way Drop is selected.
 * Round Trip keeps the website fare in bookingConfig.
 */

export const CORRIDOR_TARIFFS = {
  'coimbatore-karur': {
    id: 'coimbatore-karur',
    oneWayPackage: true,
    includedDistanceKm: 140,
    additionalKmRate: 15,
    includedExtras: ['toll', 'parking', 'permit'],
    vehicles: {
      Sedan: 2600,
      SUV: 3300,
    },
  },
  'coimbatore-pollachi': {
    id: 'coimbatore-pollachi',
    oneWayPackage: true,
    includedDistanceKm: 50,
    additionalKmRate: 30,
    extraCharges: ['toll', 'parking'],
    vehicles: {
      Sedan: 1250,
      SUV: 3000,
    },
  },
  // Both Tiruppur directions use this package. There is no separate reverse tariff.
  'coimbatore-tiruppur': {
    id: 'coimbatore-tiruppur',
    oneWayPackage: true,
    includedDistanceKm: 60,
    additionalKmRate: 30,
    extraCharges: ['toll', 'parking'],
    vehicles: {
      Sedan: 1500,
      SUV: 3000,
    },
  },
};

const packageChargeCopy = (tariff) => {
  const included = tariff.includedExtras || [];
  const extra = tariff.extraCharges || [];

  if (included.includes('permit')) {
    return {
      chargeSummaryLabel: 'Toll, parking, permit',
      chargeSummaryValue: 'Included',
      chargeNote: `Toll, parking and permit are included. Distance above ${tariff.includedDistanceKm} km is ₹${tariff.additionalKmRate} per km. This package applies to One Way Drop only.`,
      bookingNotes: `One-way package. Toll, parking and permit included. Extra distance ₹${tariff.additionalKmRate}/km beyond ${tariff.includedDistanceKm} km.`,
      detailsCharge: 'Toll, parking and permit: Included',
    };
  }

  if (extra.includes('toll') && extra.includes('parking')) {
    return {
      chargeSummaryLabel: 'Toll and parking',
      chargeSummaryValue: 'Extra',
      chargeNote: `Toll and parking are extra and are not included in the package total. Distance above ${tariff.includedDistanceKm} km is ₹${tariff.additionalKmRate} per km. This package applies to One Way Drop only.`,
      bookingNotes: `One-way package. Toll and parking extra. Extra distance ₹${tariff.additionalKmRate}/km beyond ${tariff.includedDistanceKm} km.`,
      detailsCharge: 'Toll and parking: Extra',
    };
  }

  return {
    chargeSummaryLabel: 'Toll and parking',
    chargeSummaryValue: 'Extra',
    chargeNote: `Toll and parking are extra. Distance above ${tariff.includedDistanceKm} km is ₹${tariff.additionalKmRate} per km. This package applies to One Way Drop only.`,
    bookingNotes: `One-way package. Toll and parking extra. Extra distance ₹${tariff.additionalKmRate}/km beyond ${tariff.includedDistanceKm} km.`,
    detailsCharge: 'Toll and parking: Extra',
  };
};

export const formatInr = (amount) => `₹${Number(amount).toLocaleString('en-IN')}`;

export const getPublishedFare = (tariff, vehicleType) => {
  if (!tariff || tariff.vehicles[vehicleType] == null) return null;
  return tariff.vehicles[vehicleType];
};

export const listPublishedFares = (tariff) => (
  Object.entries(tariff?.vehicles || {}).map(([vehicleType, amount]) => ({
    vehicleType,
    amount,
    label: formatInr(amount),
  }))
);

/**
 * One-way package total. Actual kilometres at or under the included
 * distance stay at the package price. Only the excess is charged.
 * Included extras stay inside the package. Listed extra charges are
 * not given an amount and are not added to the total.
 */
export const quoteOneWayPackage = (tariff, vehicleType, actualDistanceKm) => {
  if (!tariff?.oneWayPackage) return null;
  const base = tariff.vehicles?.[vehicleType];
  if (base == null) return null;

  const includedDistanceKm = tariff.includedDistanceKm;
  const extraRate = tariff.additionalKmRate;
  const actual = Number(actualDistanceKm);
  const measuredKm = Number.isFinite(actual) && actual > 0 ? actual : 0;
  const rawExtraKm = measuredKm > includedDistanceKm ? measuredKm - includedDistanceKm : 0;
  const extraKm = Math.round(rawExtraKm * 10) / 10;
  const extraFare = Math.round(extraKm * extraRate);
  const charges = packageChargeCopy(tariff);

  return {
    base,
    extraKm,
    extraFare,
    total: base + extraFare,
    includedDistanceKm,
    extraRate,
    includedExtras: tariff.includedExtras || [],
    extraCharges: tariff.extraCharges || [],
    distanceKnown: measuredKm > 0,
    ...charges,
  };
};

/** Charge notes shared by both directions of a corridor. */
export const describeCorridorCharges = (tariff) => {
  if (!tariff) return [];

  const notes = [];
  const published = listPublishedFares(tariff);

  if (tariff.oneWayPackage) {
    const fareList = published.map((item) => `${item.vehicleType} ${item.label}`).join(' and ');
    notes.push(`One-way package: ${fareList}. Each fare includes ${tariff.includedDistanceKm} km.`);
    notes.push(`Distance beyond ${tariff.includedDistanceKm} km is ${formatInr(tariff.additionalKmRate)} per km.`);
    if ((tariff.includedExtras || []).includes('permit')) {
      notes.push('Toll, parking, and applicable permit charges are included in the one-way package and are not added again.');
    } else if ((tariff.extraCharges || []).includes('toll')) {
      notes.push('Toll and parking are extra. They are not included in the package price, and this page does not add a separate toll or parking amount.');
    }
    notes.push('Round Trip does not use this package. It uses the website’s existing round-trip fare.');
    return notes;
  }

  if (published.length === 0) {
    notes.push('No fixed one-way total is published for this route. Use the booking form for a fare estimate, or call or WhatsApp Easy Drop Taxi to enquire.');
  } else if (tariff.includedDistanceKm) {
    const fareList = published.map((item) => `${item.vehicleType} ${item.label}`).join(' and ');
    notes.push(`Published one-way fare: ${fareList}. That amount covers ${tariff.includedDistanceKm} km.`);
  }

  if (tariff.freeWaitingMinutes) {
    notes.push(`${tariff.freeWaitingMinutes} minutes of waiting are included. Waiting beyond that is charged separately.`);
  } else {
    notes.push('A free-waiting allowance is not published for this route. Waiting time may be charged separately.');
  }

  if (tariff.additionalKmRate) {
    notes.push(`Kilometres beyond the included distance are charged at ${formatInr(tariff.additionalKmRate)} per km.`);
  } else if (tariff.includedDistanceKm) {
    notes.push('A rate for kilometres beyond the included distance is not published. Confirm extra distance with Easy Drop Taxi before travel.');
  }

  notes.push('Toll, parking, and permit charges are extra. They are not included in the published route fare.');
  notes.push('Driver bata is added inside the website booking estimate. These published corridor fares do not itemise driver allowance, so confirm the amount if the form estimate and the published fare differ.');

  return notes;
};
